import React, { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  HeartHandshake,
  User,
  Users,
  Bus,
  UtensilsCrossed,
  Wrench,
  Target,
  Trophy,
  ChevronRight,
  Lock,
} from 'lucide-react';

/**
 * CareSupportLanding — Phase 1 (reduced scope, 9 cards).
 *
 * Purpose:
 *   Hybrid landing dashboard for the Participant → Care & Support parent tab.
 *   Provides navigation + safe summary only. NOT a second Care Plan / Goals /
 *   Outcomes / Tasks / Checklist engine.
 *
 * Data source:
 *   Reads exclusively from the existing GET /api/participants/{id}/overview
 *   payload (fetched here once, no per-card fetch). Cards that need extra
 *   detail deep-link to the authoritative existing tabs — they DO NOT fetch
 *   additional data.
 *
 * Deferred (NOT rendered):
 *   - Daily Checklists (shift-checklist surfacing parked)
 *   - Assigned & Recurring Tasks (Tasks RBAC redesign parked)
 *
 * Deep-link contract:
 *   - Existing leaves:  ?tab=care-support&sub=<leaf>
 *   - Domain deep-link: ?tab=care-support&sub=careplans&domain=<domain_type>
 *
 * Feature-flag governance:
 *   Rendered only when `participant_caresupport_landing_enabled` is ON.
 *   Domain cards remain disabled/informational when
 *   `participant_careplan_domains_enabled` is OFF (do NOT enable that flag
 *   to expose the cards — respect its own rollout governance).
 *   Outcomes card is hidden when `goal_outcomes_reporting_enabled` is OFF.
 */

// Domain-type map — MUST match backend/routes/careplan_domains.py DOMAIN_CATALOG keys.
const DOMAIN_CARDS = [
  { id: 'daily-living',        domain: 'daily_living',        title: 'Daily Living Supports',   icon: HeartHandshake, description: 'Support routines for daily activities.' },
  { id: 'personal-care',       domain: 'personal_care',       title: 'Personal Care',           icon: User,           description: 'Hygiene, dressing, grooming and personal-support routines.' },
  { id: 'community-access',    domain: 'community_access',    title: 'Community Access',        icon: Users,          description: 'Community participation and social engagement.' },
  { id: 'transport',           domain: 'transport',           title: 'Transport',               icon: Bus,            description: 'Transport preferences and mobility considerations.' },
  { id: 'nutrition',           domain: 'nutrition',           title: 'Nutrition & Meal Support',icon: UtensilsCrossed,description: 'Meal support, dietary needs and mealtime routines.' },
  { id: 'assistive-equipment', domain: 'assistive_equipment', title: 'Assistive Equipment',     icon: Wrench,         description: 'Equipment, aids and assistive technology.' },
];

function CardShell({ title, description, icon: Icon, statusBadge, summary, actionLabel, onClick, disabled, unavailableReason, testId }) {
  const isDisabled = !!disabled;
  return (
    <Card
      data-testid={testId}
      className={`flex flex-col ${isDisabled ? 'opacity-60' : 'hover:border-primary/60 hover:shadow-sm cursor-pointer transition-all'}`}
      onClick={isDisabled ? undefined : onClick}
      role={isDisabled ? undefined : 'button'}
      tabIndex={isDisabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick && onClick();
        }
      }}
      aria-disabled={isDisabled}
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon className="w-4 h-4 text-primary shrink-0" />}
          <CardTitle className="text-sm font-semibold truncate">{title}</CardTitle>
        </div>
        {statusBadge}
      </CardHeader>
      <CardContent className="text-sm flex-1 flex flex-col justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground mb-2">{description}</p>
          {summary}
          {isDisabled && unavailableReason && (
            <p className="text-[11px] text-slate-500 italic mt-2 flex items-center gap-1" data-testid={`${testId}-unavailable`}>
              <Lock className="w-3 h-3" /> {unavailableReason}
            </p>
          )}
        </div>
        {!isDisabled && actionLabel && (
          <div className="flex items-center justify-end text-xs text-primary font-medium" data-testid={`${testId}-action`}>
            {actionLabel} <ChevronRight className="w-3 h-3 ml-1" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function CareSupportLanding({ participantId, onOpenLeaf, onOpenDomain, featureFlags = {} }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setDenied(false);
    api.get(`/participants/${participantId}/overview`)
      .then((r) => {
        if (cancelled) return;
        setOverview(r?.data?.data || null);
      })
      .catch((e) => {
        if (cancelled) return;
        const status = e?.response?.status;
        if (status === 403) setDenied(true);
        else setError('Unable to load Care & Support summary.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [participantId]);

  const domainsFlagOn = !!featureFlags.participant_careplan_domains_enabled;
  const structuredMatrixOn = !!featureFlags.care_plan_v2_structured_matrix_enabled;
  const outcomesFlagOn = !!featureFlags.goal_outcomes_reporting_enabled;
  const domainsAvailable = domainsFlagOn && structuredMatrixOn;

  // Safe read-only summaries from the existing overview payload only.
  const carePlanSummary = useMemo(() => {
    const active = overview?.clinicalOverview?.active_plan || overview?.fundingOverview?.active_plan || null;
    const goalsList = overview?.goalsOutcomes?.goals || [];
    return {
      hasActive: !!active,
      goalsCount: goalsList.length,
    };
  }, [overview]);

  const goalsSummary = useMemo(() => {
    const goals = overview?.goalsOutcomes?.goals || [];
    const active = goals.filter((g) => (g.status || '').toUpperCase() === 'ACTIVE' || (g.status || '').toUpperCase() === 'IN_PROGRESS').length;
    const overdue = goals.filter((g) => g.is_overdue || g.overdue).length;
    return { total: goals.length, active, overdue };
  }, [overview]);

  const outcomesSummary = useMemo(() => {
    const outcomes = overview?.goalsOutcomes?.outcomes || [];
    return { total: outcomes.length };
  }, [overview]);

  if (denied) {
    return <div className="p-6 text-sm text-muted-foreground" data-testid="care-support-landing-denied">You do not have permission to view Care &amp; Support for this participant.</div>;
  }

  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="care-support-landing-loading">
        {Array.from({ length: 9 }).map((_, i) => (
          <Card key={i} className="h-32 animate-pulse bg-slate-100/60" />
        ))}
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-sm text-red-600" data-testid="care-support-landing-error">{error}</div>;
  }

  return (
    <div className="space-y-4" data-testid="care-support-landing">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {/* 1. Care Plan */}
        <CardShell
          testId="care-support-card-care-plan"
          title="Care Plan"
          icon={FileText}
          description="Current active Care Plan, historical plans and creation flow (permissions required)."
          statusBadge={carePlanSummary.hasActive ? <Badge className="bg-green-100 text-green-800 border-green-200">Active</Badge> : <Badge variant="outline" className="text-muted-foreground">No active plan</Badge>}
          summary={<p className="text-xs text-muted-foreground">Approvals, versioning and Template Selection preserved.</p>}
          actionLabel="Open Care Plans"
          onClick={() => onOpenLeaf('careplans')}
        />

        {/* 2-7. Care Plan v2 Structured Matrix domains */}
        {DOMAIN_CARDS.map((d) => (
          <CardShell
            key={d.id}
            testId={`care-support-card-${d.id}`}
            title={d.title}
            icon={d.icon}
            description={d.description}
            statusBadge={domainsAvailable ? null : <Badge variant="outline" className="text-slate-500 border-slate-300">Feature unavailable</Badge>}
            actionLabel={domainsAvailable ? 'Open in Care Plan' : undefined}
            onClick={domainsAvailable ? () => onOpenDomain(d.domain) : undefined}
            disabled={!domainsAvailable}
            unavailableReason={!domainsAvailable ? 'Care Plan v2 Structured Matrix domains are not enabled for this org.' : undefined}
          />
        ))}

        {/* 8. Goal Progress */}
        <CardShell
          testId="care-support-card-goal-progress"
          title="Goal Progress"
          icon={Target}
          description="Active goals, progress status and overdue count (read-only summary)."
          statusBadge={goalsSummary.overdue > 0 ? <Badge className="bg-orange-100 text-orange-800 border-orange-200">{goalsSummary.overdue} overdue</Badge> : null}
          summary={
            <div className="text-xs text-muted-foreground flex gap-3">
              <span data-testid="care-support-card-goal-progress-total">Total: <span className="font-medium text-foreground">{goalsSummary.total}</span></span>
              <span data-testid="care-support-card-goal-progress-active">Active: <span className="font-medium text-foreground">{goalsSummary.active}</span></span>
            </div>
          }
          actionLabel="Open Goals"
          onClick={() => onOpenLeaf('goals')}
        />

        {/* 9. Participant Outcomes — only rendered when feature is effectively enabled */}
        {outcomesFlagOn && (
          <CardShell
            testId="care-support-card-outcomes"
            title="Participant Outcomes"
            icon={Trophy}
            description="Outcome measures and reviews linked to participant goals."
            summary={
              <div className="text-xs text-muted-foreground" data-testid="care-support-card-outcomes-total">
                Recorded outcomes: <span className="font-medium text-foreground">{outcomesSummary.total}</span>
              </div>
            }
            actionLabel="Open Outcomes"
            onClick={() => onOpenLeaf('outcomes')}
          />
        )}
      </div>
    </div>
  );
}

export default CareSupportLanding;
